import { useId, useState } from 'react';

// Read the visible bounds only; the uploaded image is never rewritten or stretched.
export function visibleBrandBounds(image) {
  const scale = Math.min(1, 1024 / Math.max(image.naturalWidth, image.naturalHeight));
  const width = Math.max(1, Math.round(image.naturalWidth * scale));
  const height = Math.max(1, Math.round(image.naturalHeight * scale));
  const canvas = document.createElement('canvas');
  canvas.width = width; canvas.height = height;
  const context = canvas.getContext('2d', { willReadFrequently: true });
  if (!context) return null;
  context.drawImage(image, 0, 0, width, height);
  const { data } = context.getImageData(0, 0, width, height);
  let left = width, top = height, right = -1, bottom = -1;
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      // Ignore near-invisible export noise in otherwise transparent padding.
      if (data[(y * width + x) * 4 + 3] <= 8) continue;
      left = Math.min(left, x); top = Math.min(top, y);
      right = Math.max(right, x); bottom = Math.max(bottom, y);
    }
  }
  if (right < left) return null;
  left = Math.max(0, left - 1); top = Math.max(0, top - 1);
  right = Math.min(width, right + 2); bottom = Math.min(height, bottom + 2);
  return {
    viewBox: [left / scale, top / scale, (right - left) / scale, (bottom - top) / scale].join(' '),
    width: image.naturalWidth, height: image.naturalHeight,
  };
}

export default function BrandAsset({ src, alt, className, testId, color }) {
  const [measured, setMeasured] = useState(null);
  const tintId = useId();
  const bounds = measured?.src === src ? measured.bounds : null;
  if (bounds) return (
    <svg role="img" aria-label={alt} className={className} data-testid={testId}
      viewBox={bounds.viewBox} preserveAspectRatio="xMidYMid meet">
      {color && <defs><filter id={tintId} colorInterpolationFilters="sRGB">
        <feFlood floodColor={color} />
        <feComposite in2="SourceAlpha" operator="in" />
      </filter></defs>}
      <image href={src} width={bounds.width} height={bounds.height} filter={color ? `url(#${tintId})` : undefined} />
    </svg>
  );
  return <img src={src} alt={alt} className={`${className} object-contain`} data-testid={testId} onLoad={event => {
    try {
      const result = visibleBrandBounds(event.currentTarget);
      if (result) setMeasured({ src, bounds: result });
    } catch { /* Keep the uncropped original when pixels cannot be read. */ }
  }} />;
}
